import * as z from 'zod';
import { normalizeRbacRoute } from '../lib/normalize-route';

const id = z.number().int().positive();
const list = <T extends z.ZodType>(schema: T) =>
  z
    .array(schema)
    .nullish()
    .transform((value) => value ?? []);
// Collection keys identify a list response; missing keys must not become a false empty state.
const collection = <T extends z.ZodType>(schema: T) =>
  z
    .array(schema)
    .nullable()
    .transform((value) => value ?? []);
export const subjectSchema = z.object({
  id,
  user_type: z.string(),
  role: z.string(),
  key: z.string(),
  title: z.string().optional(),
});
export const fieldSchema = z.object({ path: z.string(), type: z.string() });
const rbacRouteObjectSchema = z.object({
  id: z.string().min(1),
  title: z.string(),
  vertical: z.string(),
  methods: list(z.string()),
  uris: list(z.string()),
  governed: z.boolean(),
  deprecated: z.boolean(),
  fields: list(fieldSchema),
});
export const rbacRouteSchema = z.preprocess(normalizeRbacRoute, rbacRouteObjectSchema);
export const routeEnvelopeSchema = z.object({ route: rbacRouteSchema });
export const grantCreateSchema = z
  .object({
    route_id: z.string().min(1),
    subject_id: id.optional(),
    user_id: z.uuid().optional(),
    deny: z.boolean().optional(),
  })
  .strict()
  .refine((value) => (value.subject_id !== undefined) !== (value.user_id !== undefined), {
    message: 'Choose exactly one subject or user',
    path: ['user_id'],
  })
  .refine((value) => !value.deny || value.user_id !== undefined, {
    message: 'Only users can have a personal deny',
    path: ['deny'],
  });
const normalizeSlug = (raw: unknown) =>
  typeof raw === 'object' && raw !== null && 'route_slug' in raw
    ? { ...raw, route_id: raw.route_slug }
    : raw;
export const grantSchema = z.preprocess(
  normalizeSlug,
  z.object({
    id,
    route_id: z.string(),
    subject_id: id.nullish(),
    user_id: z.string().nullish(),
    deny: z.boolean().optional(),
  }),
);
export const fieldRuleBodySchema = z
  .object({ subject_ids: z.array(id), user_ids: z.array(z.uuid()) })
  .strict();
export const fieldRuleSchema = z.preprocess(
  normalizeSlug,
  z.object({
    route_id: z.string(),
    path: z.string(),
    subject_ids: list(id),
    user_ids: list(z.string()),
  }),
);
export const routeDetailsSchema = z.object({
  route: rbacRouteSchema,
  grants: list(grantSchema),
  field_rules: list(fieldRuleSchema),
});
export const subjectsSchema = z.object({ subjects: collection(subjectSchema) });
export const routesSchema = z.object({ routes: collection(rbacRouteSchema) });
export const grantsSchema = z.object({ grants: collection(grantSchema) });
export const fieldRulesSchema = z.object({ field_rules: collection(fieldRuleSchema) });
export const matrixSchema = z.object({
  subjects: collection(subjectSchema),
  routes: collection(
    z.preprocess(
      normalizeRbacRoute,
      rbacRouteObjectSchema
        .pick({ id: true, title: true, vertical: true, methods: true, uris: true })
        .extend({
          granted_subject_ids: list(id),
          user_grants: z.number().int().nonnegative(),
          granted_user_ids: list(z.string()),
          denied_user_ids: list(z.string()),
        }),
    ),
  ),
});
export const auditPageSchema = z.object({
  entries: collection(
    z.object({
      id,
      at: z.string(),
      actor_user_id: z.string(),
      action: z.string(),
      entity: z.string(),
      // Grant seed events contain a collection of changes, while individual
      // grant events contain a single change object.
      before: z
        .union([z.record(z.string(), z.unknown()), z.array(z.unknown())])
        .nullish(),
      after: z.union([z.record(z.string(), z.unknown()), z.array(z.unknown())]).nullish(),
    }),
  ),
  next_before_id: id.nullish().transform((value) => value ?? null),
});
export const FEATURE_USER_TYPES = ['user', 'merchant'] as const;
export const FEATURE_KEY_PATTERN = /^[A-Za-z0-9._:/-]{1,128}$/;
export const featureRouteSchema = z.object({
  slug: z.string(),
  title: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  methods: list(z.string()),
  uris: list(z.string()),
  deprecated: z.boolean().optional().default(false),
  governed: z.boolean().optional().default(true),
});
export const featureSchema = z.object({
  id,
  user_type: z.string(),
  key: z.string(),
  title: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  description: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  broken: z.boolean().optional().default(false),
  routes: list(featureRouteSchema),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});
export const featureInputSchema = z
  .object({
    user_type: z.enum(FEATURE_USER_TYPES),
    key: z.string().regex(FEATURE_KEY_PATTERN),
    title: z.string().trim().optional(),
    description: z.string().trim().optional(),
    route_slugs: z.array(z.string().min(1)).min(1),
  })
  .strict();
export const featurePatchSchema = z
  .object({
    title: z.string().trim().optional(),
    description: z.string().trim().optional(),
    route_slugs: z.array(z.string().min(1)).min(1).optional(),
  })
  .strict();
export const routeTitleSchema = z.object({ title: z.string().trim() }).strict();
export type Feature = z.infer<typeof featureSchema>;
export type FeatureRoute = z.infer<typeof featureRouteSchema>;
export type FeatureInput = z.infer<typeof featureInputSchema>;
export type FeaturePatch = z.infer<typeof featurePatchSchema>;
export type RbacSubject = z.infer<typeof subjectSchema>;
export type RbacRoute = z.infer<typeof rbacRouteSchema>;
export type RbacGrant = z.infer<typeof grantSchema>;
export type GrantCreate = z.infer<typeof grantCreateSchema>;
export type FieldRuleBody = z.infer<typeof fieldRuleBodySchema>;

export type RbacMatrixRow = z.infer<typeof matrixSchema>['routes'][number];
export type RbacAuditEntry = z.infer<typeof auditPageSchema>['entries'][number];
